import { z } from 'zod';

export const businessInfoSchema = z.object({
  name: z.string().min(1).max(120),
  phone: z.string().max(40).optional(),
  email: z.string().email().optional(),
  address: z
    .object({
      street: z.string().min(1).max(200),
      city: z.string().min(1).max(100),
      state: z.string().min(1).max(100),
      zip: z.string().min(1).max(20),
      country: z.string().max(100).optional(),
    })
    .optional(),
  description: z.string().max(2000).optional(),
  socialLinks: z
    .object({
      facebook: z.string().url().optional(),
      instagram: z.string().url().optional(),
      twitter: z.string().url().optional(),
      yelp: z.string().url().optional(),
    })
    .optional(),
});

export const menuItemSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  price: z.number().int().min(0),
  imageKey: z.string().min(1).optional(),
  category: z.string().max(80).optional(),
});

export const menuModuleSchema = z.object({
  categories: z.array(z.string().max(80)).optional(),
  items: z.array(menuItemSchema),
});

const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;

export const dayHoursSchema = z
  .object({
    closed: z.boolean(),
    open: z.string().regex(timeRegex).optional(),
    close: z.string().regex(timeRegex).optional(),
  })
  .refine((v) => v.closed || (Boolean(v.open) && Boolean(v.close)), 'open and close are required when not closed');

export const hoursModuleSchema = z.object({
  monday: dayHoursSchema,
  tuesday: dayHoursSchema,
  wednesday: dayHoursSchema,
  thursday: dayHoursSchema,
  friday: dayHoursSchema,
  saturday: dayHoursSchema,
  sunday: dayHoursSchema,
});

export const galleryImageSchema = z.object({
  id: z.string(),
  key: z.string().min(1),
  alt: z.string().max(200).optional(),
  caption: z.string().max(300).optional(),
});

export const galleryModuleSchema = z.object({
  images: z.array(galleryImageSchema),
});

export const heroModuleSchema = z.object({
  imageKey: z.string().min(1),
  headline: z.string().max(160).optional(),
  subheadline: z.string().max(300).optional(),
  ctaLabel: z.string().max(60).optional(),
  ctaHref: z
    .string()
    .max(500)
    .refine((v) => /^\/(?!\/)/.test(v) || /^https?:\/\//i.test(v), {
      message: 'ctaHref must be a relative path or an http(s) URL',
    })
    .optional(),
  alt: z.string().max(200).optional(),
});

export const announcementModuleSchema = z.object({
  enabled: z.boolean(),
  title: z.string().max(120).optional(),
  message: z.string().min(1).max(500),
  link: z
    .object({
      label: z.string().min(1).max(60),
      url: z.string().url(),
    })
    .optional(),
  variant: z.enum(['info', 'warning', 'success']).optional(),
});

export const serviceItemSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  price: z.number().int().min(0).optional(),
  durationMinutes: z.number().int().min(0).optional(),
});

export const servicesModuleSchema = z.object({
  items: z.array(serviceItemSchema),
});

export const moduleSchemas = {
  'business-info': businessInfoSchema,
  hero: heroModuleSchema,
  menu: menuModuleSchema,
  hours: hoursModuleSchema,
  gallery: galleryModuleSchema,
  announcement: announcementModuleSchema,
  services: servicesModuleSchema,
} as const;
